"use client"

import { useState, useEffect } from "react"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  Chip,
  Divider,
  CircularProgress,
} from "@mui/material"
import {
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Payment as PaymentIcon,
  Home as HomeIcon,
  BarChart as BarChartIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts"
import axios from "axios"

// Couleur principale de l'application (même que le header)
const primaryColor = "#003366"
const primaryColorLight = "#004080"
const primaryColorLighter = "#0055b3"
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user ? user.id : null;
// Configuration de l'API
const API_BASE_URL = process.env.REACT_APP_APP_URL

// Création de l'instance axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Intercepteur pour ajouter le token d'authentification
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("CC_Token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Services API pour les chevaux et leurs poids
const horseService = {
  // Récupérer tous les chevaux
  getAllHorses: () => {
    return api.get("/api/horses")
  },

  // Récupérer l'historique des poids d'un cheval
  getHorseWeightHistory: (horseId) => {
    return api.get(`/api/horses/${horseId}/weight-history`)
  },

  // Ajouter un nouveau poids pour un cheval
  addHorseWeight: (horseId, weightData) => {
    return api.post(`/api/horses/${horseId}/weight`, weightData)
  },

  // Supprimer un enregistrement de poids
  deleteHorseWeight: (weightId) => {
    return api.delete(`/api/horse-weights/${weightId}`)
  },
}

// Fonctions utilitaires pour formater les données
const formatDate = (dateString) => {
  if (!dateString) return "-"
  const date = new Date(dateString)
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
}

// Fonction pour formater les données pour le graphique
const formatWeightHistoryForChart = (weightHistory) => {
  if (!weightHistory || !Array.isArray(weightHistory) || weightHistory.length === 0) {
    return []
  }

  // Trier par date (du plus ancien au plus récent)
  const sortedHistory = [...weightHistory].sort((a, b) => new Date(a.date) - new Date(b.date))

  return sortedHistory.map((record) => ({
    date: formatDate(record.date),
    weight: Number.parseFloat(record.calculated_weight),
    hg: Number.parseFloat(record.hg),
    pt: Number.parseFloat(record.pt),
  }))
}

// Fonction pour calculer les statistiques de poids
const calculateWeightStats = (weightHistory) => {
  if (!weightHistory || !Array.isArray(weightHistory) || weightHistory.length === 0) {
    return {
      min: 0,
      max: 0,
      avg: 0,
      variation: 0,
    }
  }

  const weights = weightHistory.map((record) => Number.parseFloat(record.calculated_weight))
  const min = Math.min(...weights)
  const max = Math.max(...weights)
  const avg = Math.round(weights.reduce((a, b) => a + b, 0) / weights.length)
  const variation = max - min

  return {
    min,
    max,
    avg,
    variation,
  }
}

// Composant principal
const Suivichevaux = () => {
  const navigate = useNavigate()
  const [openModal, setOpenModal] = useState(false)
  const [selectedHorse, setSelectedHorse] = useState(null)
  const [tabValue, setTabValue] = useState(0)
  const [horses, setHorses] = useState([])
  const [weightHistory, setWeightHistory] = useState([])
  const [weightStats, setWeightStats] = useState({
    min: 0,
    max: 0,
    avg: 0,
    variation: 0,
  })
  const [loading, setLoading] = useState(true)
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Charger les chevaux au chargement du composant
  useEffect(() => {
    fetchHorses()
  }, [])

  // Fonction pour récupérer tous les chevaux
  const fetchHorses = async () => {
    try {
      setLoading(true)
      const response = await horseService.getAllHorses()
      console.log("API Response:", response)

      if (response.data && response.data.data) {
        setHorses(response.data.data)
      } else {
        toast.error("Format de réponse API inattendu")
      }
    } catch (error) {
      console.error("Erreur lors du chargement des chevaux:", error)
      toast.error("Erreur lors du chargement des chevaux")
    } finally {
      setLoading(false)
    }
  }

  // Fonction pour récupérer l'historique des poids d'un cheval
  const fetchHorseWeightHistory = async (horseId) => {
    try {
      setLoadingHistory(true)
      const response = await horseService.getHorseWeightHistory(horseId)
      console.log("Weight History Response:", response)

      if (response.data && response.data.data) {
        const formattedHistory = formatWeightHistoryForChart(response.data.data)
        setWeightHistory(formattedHistory)
        setWeightStats(calculateWeightStats(response.data.data))
      } else {
        setWeightHistory([])
        setWeightStats({
          min: 0,
          max: 0,
          avg: 0,
          variation: 0,
        })
        toast.warning("Aucun historique de poids disponible")
      }
    } catch (error) {
      console.error("Erreur lors du chargement de l'historique des poids:", error)
      toast.error("Erreur lors du chargement de l'historique des poids")
      setWeightHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  // Fonction pour ouvrir le modal avec les données du cheval sélectionné
  const handleOpenModal = async (horse) => {
    setSelectedHorse(horse)
    setOpenModal(true)
    await fetchHorseWeightHistory(horse.id)
  }

  // Fonction pour fermer le modal
  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedHorse(null)
    setTabValue(0)
    setWeightHistory([])
  }

  // Fonction pour changer d'onglet
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    })
    setTimeout(() => navigate("/login"), 4000) // Redirect after toast
  }

  const handleNavigateToNotifications = () => {
      user.role === "admin" ? navigate("/AdminGetNotifs") : navigate("/notifications");
  }

  const handleProfile = () => {
  user.role === "admin" ? navigate("/ProfileAdmin") : navigate("/Profile");
  }

  // Formater les poids pour l'affichage dans le tooltip
  const formatTooltip = (value) => {
    if (value === null || value === undefined) return "-"
    return `${value} kg`
  }

  // Générer les lignes de référence principales (tous les 100kg)
  const generateMajorReferenceLines = () => {
    const lines = []
    for (let i = 300; i <= 700; i += 100) {
      lines.push(
        <ReferenceLine
          key={`major-${i}`}
          y={i}
          stroke="#666666"
          strokeWidth={1}
          label={{ value: `${i} kg`, position: "right", fill: "#666", fontSize: 12 }}
        />,
      )
    }
    return lines
  }

  // Générer les lignes de référence secondaires (tous les 50kg qui ne sont pas des multiples de 100)
  const generateMinorReferenceLines = () => {
    const lines = []
    for (let i = 350; i <= 650; i += 100) {
      if (i % 100 !== 0) {
        lines.push(<ReferenceLine key={`minor-${i}`} y={i} stroke="#cccccc" strokeDasharray="3 3" />)
      }
    }
    return lines
  }

  return (
    <div className="app">
      {/* Toast Container */}
      <ToastContainer />

      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: primaryColor }}>
        <Toolbar>
          <Avatar alt="Logo" src={"../assets/cej.png"} sx={{ width: 40, height: 40, marginRight: "0.5rem" }} />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content Area */}
      <Box
        sx={{
          padding: "0.5rem",
          marginTop: "64px", // Space below header
          backgroundColor: "#e0f7fa",
          height: "calc(100vh - 64px - 64px)", // 100vh - Header - Footer
          overflowY: "auto", // Allows internal scrolling if needed
        }}
      >
        {/* Titre du tableau */}
        <Typography
          variant="h5"
          sx={{ textAlign: "center", margin: "1rem 0", color: primaryColor, fontWeight: "bold" }}
        >
          Suivi des Chevaux
        </Typography>

        {/* Tableau des chevaux */}
        <TableContainer
          component={Paper}
          sx={{
            maxWidth: "100%",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Table aria-label="tableau des chevaux">
            <TableHead>
              <TableRow sx={{ background: `linear-gradient(45deg, ${primaryColor} 30%, ${primaryColorLight} 90%)` }}>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    padding: "16px 24px",
                    borderBottom: "none",
                  }}
                >
                  Nom du Cheval
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    padding: "16px 24px",
                    borderBottom: "none",
                  }}
                >
                  Poids
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: primaryColor }} />
                    <Typography sx={{ mt: 2, color: primaryColor }}>Chargement des données...</Typography>
                  </TableCell>
                </TableRow>
              ) : horses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                      Aucun cheval trouvé
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                horses.map((horse, index) => (
                  <TableRow
                    key={horse.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "rgba(0, 51, 102, 0.05)" : "white",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgba(0, 51, 102, 0.1)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
                      },
                      borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        padding: "16px 24px",
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: primaryColor,
                          width: 48,
                          height: 48,
                          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                          fontSize: "28px",
                        }}
                      >
                        🐴
                      </Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: "600", fontSize: "1.1rem", color: primaryColor }}>
                          {horse.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Propriétaire: {horse.owner}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ padding: "16px 24px" }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                        <Chip
                          label={`${horse.weight || "N/A"} kg`}
                          sx={{
                            backgroundColor: primaryColor,
                            color: "white",
                            fontWeight: "bold",
                            mb: 1,
                          }}
                        />
                        <Button
                          variant="contained"
                          startIcon={<BarChartIcon />}
                          onClick={() => handleOpenModal(horse)}
                          aria-label={`Voir le poids de ${horse.name}`}
                          sx={{
                            background: `linear-gradient(45deg, ${primaryColor} 30%, ${primaryColorLight} 90%)`,
                            color: "white",
                            borderRadius: "24px",
                            padding: "8px 24px",
                            textTransform: "none",
                            fontWeight: "bold",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: `linear-gradient(45deg, ${primaryColor} 30%, ${primaryColorLighter} 90%)`,
                              boxShadow: "0 6px 15px rgba(0,0,0,0.2)",
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          Historique
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Modal pour afficher le poids par mois */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            overflow: "hidden",
          },
        }}
      >
        {selectedHorse && (
          <>
            <DialogTitle
              sx={{
                background: `linear-gradient(45deg, ${primaryColor} 30%, ${primaryColorLight} 90%)`,
                color: "white",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 24px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: "white",
                    width: 48,
                    height: 48,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    fontSize: "28px",
                  }}
                >
                  🐴
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Poids de {selectedHorse.name}
                </Typography>
              </Box>
              <IconButton edge="end" sx={{ color: "white" }} onClick={handleCloseModal} aria-label="fermer">
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            {/* Onglets pour basculer entre graphique et tableau */}
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: "500",
                  py: 2,
                },
                "& .Mui-selected": {
                  color: primaryColor,
                  fontWeight: "bold",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: primaryColor,
                  height: 3,
                },
              }}
            >
              <Tab
                icon={<ShowChartIcon />}
                label="Graphique"
                sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}
              />
              <Tab
                icon={<TableChartIcon />}
                label="Tableau"
                sx={{ display: "flex", flexDirection: "row", gap: 1, alignItems: "center" }}
              />
            </Tabs>

            <DialogContent dividers sx={{ padding: 3 }}>
              {loadingHistory ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 400,
                  }}
                >
                  <CircularProgress sx={{ color: primaryColor }} />
                  <Typography sx={{ mt: 2, color: primaryColor }}>Chargement de l'historique...</Typography>
                </Box>
              ) : weightHistory.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 400,
                  }}
                >
                  <Typography variant="h6" sx={{ color: "text.secondary" }}>
                    Aucun historique de poids disponible
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                    Ajoutez des mesures de poids pour voir l'évolution
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Contenu de l'onglet Graphique */}
                  {tabValue === 0 && (
                    <Box sx={{ height: 400, width: "100%" }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={weightHistory}
                          margin={{
                            top: 20,
                            right: 50,
                            left: 20,
                            bottom: 70,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis
                            dataKey="date"
                            angle={-45}
                            textAnchor="end"
                            height={70}
                            interval={0}
                            tick={{ fontSize: 12, fill: "#666" }}
                            tickLine={{ stroke: "#ccc" }}
                            axisLine={{ stroke: "#ccc" }}
                          />
                          <YAxis
                            domain={[300, 700]}
                            ticks={[300, 350, 400, 450, 500, 550, 600, 650, 700]}
                            label={{
                              value: "Poids (kg)",
                              angle: -90,
                              position: "insideLeft",
                              offset: -5,
                              style: { fill: "#666", fontSize: 14 },
                            }}
                            tick={{ fontSize: 12, fill: "#666" }}
                            tickLine={{ stroke: "#ccc" }}
                            axisLine={{ stroke: "#ccc" }}
                          />
                          <Tooltip
                            formatter={formatTooltip}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                              border: `1px solid ${primaryColor}`,
                              borderRadius: "8px",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                            }}
                            labelStyle={{ color: primaryColor, fontWeight: "bold" }}
                          />

                          {/* Lignes de référence */}
                          {generateMinorReferenceLines()}
                          {generateMajorReferenceLines()}

                          <Line
                            type="monotone"
                            dataKey="weight"
                            stroke={primaryColor}
                            activeDot={{ r: 8, fill: primaryColor, stroke: "white", strokeWidth: 2 }}
                            strokeWidth={3}
                            name="Poids"
                            connectNulls={true}
                            dot={{
                              stroke: primaryColor,
                              strokeWidth: 2,
                              r: 5,
                              fill: "white",
                            }}
                            animationDuration={1500}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  {/* Contenu de l'onglet Tableau */}
                  {tabValue === 1 && (
                    <TableContainer
                      sx={{ maxHeight: 400, borderRadius: "8px", boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}
                    >
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: primaryColor,
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "1rem",
                              }}
                            >
                              Date
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                backgroundColor: primaryColor,
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "1rem",
                              }}
                            >
                              HG (cm)
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                backgroundColor: primaryColor,
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "1rem",
                              }}
                            >
                              PT (cm)
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                backgroundColor: primaryColor,
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "1rem",
                              }}
                            >
                              Poids (kg)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {weightHistory.map((record, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                backgroundColor: index % 2 === 0 ? "rgba(0, 51, 102, 0.05)" : "white",
                                "&:hover": {
                                  backgroundColor: "rgba(0, 51, 102, 0.1)",
                                },
                              }}
                            >
                              <TableCell component="th" scope="row" sx={{ fontWeight: "500" }}>
                                {record.date}
                              </TableCell>
                              <TableCell align="right">{record.hg}</TableCell>
                              <TableCell align="right">{record.pt}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${record.weight} kg`}
                                  sx={{
                                    backgroundColor: primaryColor,
                                    color: "white",
                                    fontWeight: "bold",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Résumé des statistiques */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: "rgba(0, 51, 102, 0.05)", borderRadius: "8px" }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: primaryColor }}>
                      Statistiques
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-around", flexWrap: "wrap", gap: 2 }}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Poids minimum
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor }}>
                          {weightStats.min} kg
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Poids moyen
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor }}>
                          {weightStats.avg} kg
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Poids maximum
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor }}>
                          {weightStats.max} kg
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Variation
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: primaryColor }}>
                          {weightStats.variation} kg
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions sx={{ padding: 2, backgroundColor: "#f9f9f9" }}>
              <Button
                onClick={handleCloseModal}
                variant="contained"
                sx={{
                  backgroundColor: primaryColor,
                  "&:hover": {
                    backgroundColor: primaryColorLight,
                  },
                  borderRadius: "20px",
                  textTransform: "none",
                  px: 4,
                  py: 1,
                  fontWeight: "bold",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              >
                Fermer
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Footer */}
      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: primaryColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
           onClick={() => navigate(user.role === "admin" ? "/AdminChrono" : "/emploi")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate(user.role === "admin" ? "/admin" : "/home")}
          />
          <BottomNavigationAction
            label="Paiement"
            icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
           onClick={() => navigate(user.role === "admin" ? "/AdminPaiment" : "/payment")}
          />
        </BottomNavigation>
      </Box>
    </div>
  )
}

export default Suivichevaux
